import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { clubService } from '../../services/clubService';
import { SubscriptionTier } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
`;

const SubscribeCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  padding: 40px;
  width: 100%;
  max-width: 900px;

  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #718096;
  margin-bottom: 32px;
  text-align: center;
`;

const TiersContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 24px;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TierCard = styled.div<{ $selected: boolean; $recommended?: boolean }>`
  border: 3px solid ${(props) => (props.$selected ? '#667eea' : '#e2e8f0')};
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  background: ${(props) => (props.$selected ? '#f7faff' : 'white')};

  ${(props) =>
    props.$recommended &&
    `
    border-color: #48bb78;
    &::before {
      content: 'RECOMMENDED';
      position: absolute;
      top: -12px;
      left: 50%;
      transform: translateX(-50%);
      background: #48bb78;
      color: white;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.5px;
    }
  `}

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }
`;

const TierName = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 8px;
`;

const TierLimit = styled.p`
  font-size: 16px;
  color: #4a5568;
  margin-bottom: 16px;
`;

const TierFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const TierFeature = styled.li`
  font-size: 14px;
  color: #718096;
  margin-bottom: 8px;
  padding-left: 24px;
  position: relative;

  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #48bb78;
    font-weight: 700;
  }
`;

const DiscountSection = styled.div`
  margin-bottom: 32px;
  padding: 20px;
  background: #f7fafc;
  border-radius: 8px;
`;

const DiscountLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 8px;
`;

const DiscountInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &:disabled {
    background-color: #edf2f7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background-color: #fff5f5;
  border: 1px solid #fc8181;
  border-radius: 8px;
  padding: 12px 16px;
  color: #c53030;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const WarningMessage = styled.div`
  background-color: #fffaf0;
  border: 1px solid #f6ad55;
  border-radius: 8px;
  padding: 12px 16px;
  color: #c05621;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 14px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SkipButton = styled.button`
  width: 100%;
  background: transparent;
  color: #718096;
  padding: 12px 24px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;

  &:hover:not(:disabled) {
    border-color: #cbd5e0;
    color: #4a5568;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TIER_INFO = {
  [SubscriptionTier.STARTER]: {
    name: 'Starter',
    teamLimit: 3,
    features: [
      'Up to 3 teams',
      'Unlimited players per team',
      'Full assessment tracking',
      'Progress reports',
      'Email support',
    ],
    recommended: false,
  },
  [SubscriptionTier.STANDARD]: {
    name: 'Standard',
    teamLimit: 10,
    features: [
      'Up to 10 teams',
      'Unlimited players per team',
      'Full assessment tracking',
      'Progress reports',
      'Priority email support',
      'Advanced analytics',
    ],
    recommended: true,
  },
  [SubscriptionTier.PREMIUM]: {
    name: 'Premium',
    teamLimit: 50,
    features: [
      'Up to 50 teams',
      'Unlimited players per team',
      'Full assessment tracking',
      'Progress reports',
      'Priority support',
      'Advanced analytics',
      'Custom branding',
      'API access',
    ],
    recommended: false,
  },
};

const AdminSubscribePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier | null>(null);
  const [discountCode, setDiscountCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);

  useEffect(() => {
    // Check if user cancelled checkout
    if (searchParams.get('cancelled') === 'true') {
      setCancelled(true);
    }
  }, [searchParams]);

  const handleTierSelect = (tier: SubscriptionTier) => {
    setSelectedTier(tier);
    if (error) setError(null);
  };

  const handleSubscribe = async () => {
    if (!selectedTier) {
      setError('Please select a subscription tier');
      return;
    }

    if (!user?.profile?.clubId) {
      setError('No club ID found. Please complete club setup first.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call POST /api/clubs/:clubId/checkout-session
      const response = await clubService.createCheckoutSession(
        user.profile.clubId,
        selectedTier,
        discountCode.trim() || undefined
      );

      // Redirect to Stripe Checkout URL
      if (response.url) {
        window.location.href = response.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err: any) {
      console.error('Checkout session creation failed:', err);
      
      if (err.response?.data?.error?.code === 'CHECKOUT_SESSION_FAILED') {
        setError('Failed to create checkout session. Please try again.');
      } else if (err.response?.data?.error?.message) {
        setError(err.response.data.error.message);
      } else {
        setError('Failed to start subscription process. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleSkip = () => {
    // Allow user to skip subscription and go to dashboard
    // They won't be able to create teams without a subscription
    navigate('/administration-club/dashboard');
  };

  return (
    <Container>
      <SubscribeCard>
        <Title>Choose Your Subscription</Title>
        <Subtitle>Select the plan that best fits your club's needs</Subtitle>

        {cancelled && (
          <WarningMessage>
            <span>ℹ️</span>
            <span>Checkout was cancelled. You can try again or skip for now.</span>
          </WarningMessage>
        )}

        {error && (
          <ErrorMessage>
            <span>⚠️</span>
            <span>{error}</span>
          </ErrorMessage>
        )}

        <TiersContainer>
          {Object.entries(TIER_INFO).map(([tier, info]) => (
            <TierCard
              key={tier}
              $selected={selectedTier === tier}
              $recommended={info.recommended}
              onClick={() => handleTierSelect(tier as SubscriptionTier)}
            >
              <TierName>{info.name}</TierName>
              <TierLimit>{info.teamLimit} teams</TierLimit>
              <TierFeatures>
                {info.features.map((feature, index) => (
                  <TierFeature key={index}>{feature}</TierFeature>
                ))}
              </TierFeatures>
            </TierCard>
          ))}
        </TiersContainer>

        <DiscountSection>
          <DiscountLabel htmlFor="discountCode">
            Discount Code (Optional)
          </DiscountLabel>
          <DiscountInput
            id="discountCode"
            type="text"
            value={discountCode}
            onChange={(e) => setDiscountCode(e.target.value)}
            placeholder="Enter discount code"
            disabled={loading}
          />
        </DiscountSection>

        <SubmitButton onClick={handleSubscribe} disabled={loading || !selectedTier}>
          {loading ? <LoadingSpinner /> : 'Continue to Payment'}
        </SubmitButton>

        <SkipButton onClick={handleSkip} disabled={loading}>
          Skip for Now
        </SkipButton>
      </SubscribeCard>
    </Container>
  );
};

export default AdminSubscribePage;
